import Link from 'next/link';

// Static, JavaScript-free first frame of the homepage hero (GB #1486 / P1.1,
// site-speed plan 2026-07-17, SPEC-P1.1-home-hero-cls.md).
//
// WHY THIS EXISTS: `useMediaQuery` returns false during server rendering and on
// the first client render, so the homepage used to server-render only the
// DESKTOP carousel. Every phone received the desktop hero markup, downloaded
// the desktop banner, threw that markup away once React hydrated, and only THEN
// began fetching the real mobile hero — the page's Largest Contentful Paint
// element. That made mobile LCP wait for hydration to finish no matter how fast
// the image itself arrived. This component puts the CORRECT slide-1 hero in the
// server HTML for BOTH breakpoints, letting the browser pick exactly one image
// via <picture>/<source media>. The interactive carousel (Swiper on mobile,
// react-slick on desktop) replaces this frame after hydration inside a box of
// identical dimensions, so the swap causes no layout shift.
//
// KEEP IN SYNC — this frame is a hand-copy of slide 1 of both carousels. If
// `banners[0]` changes in MobileHero.tsx or HomepageHeader.tsx (image, headline,
// subtitle, button text, button link, text colour, titleSizeClass), change it
// here AND in the two media-gated <link rel="preload"> tags in pages/index.tsx.
// The three places carry the same two URLs and must not drift apart.
// (titleSizeClass, added GB #1486, is a per-slide headline font-size override
// in MobileHero.tsx's `banners` array — slide 1 does not set it today, so this
// frame's hardcoded `text-[2.375rem]` headline size is still correct as-is.
// If slide 1 ever gets its own titleSizeClass, mirror that size here too.)
//
// COUPLING TO PHASE 2 (Bunny image transforms): if these image URLs are ever
// rewritten to Bunny-transformed URLs, this component and the preloads in
// pages/index.tsx must be updated in the same change. Miss either and the LCP
// image double-downloads: the preloads fetch the old URLs while the markup
// fetches the new ones.
//
// The classes below are verbatim clones of each carousel's slide 1 — mobile
// styles as the base, desktop styles as `md:` overrides. Do not "improve" them;
// visual parity with slide 1 is what makes the post-hydration swap invisible.
const HeroFirstFrame = () => {
  return (
    <header className="relative h-[380px] w-full select-none overflow-hidden md:h-[32vw] md:max-h-[500px] md:bg-dark">
      {/* The same single canonical H1 both carousels render. Keeping it here
          (rather than in the slide markup) means the page has exactly one H1
          in the server HTML and still exactly one after the carousel mounts. */}
      <h1 className="sr-only">
        GearFocus — buy and sell used cameras, lenses, and cinema gear from
        verified sellers, with buyer protection on every order.
      </h1>

      {/* <picture> lets the BROWSER choose exactly one image by media query —
          no JavaScript, no double download. Deliberately a plain <img> rather
          than next/image: `images.unoptimized` is true in next.config.js so
          next/image adds nothing here, and its `priority` prop would inject a
          preload with NO media attribute — the exact cross-breakpoint download
          this component exists to remove. The two media-gated preloads in
          pages/index.tsx are the only sanctioned hero preloads. */}
      <picture>
        <source media="(width < 48rem)" srcSet="/banners/mobile-banner-2.webp" />
        <img
          src="/banners/Gearfocus_WebsiteHeaders2.webp"
          alt="Used Gear. Verified Sellers. Protected Orders. - Shop thousands of cameras, lenses, and cinema rigs from sellers we verify one by one."
          className="absolute inset-0 h-full w-full object-cover"
          decoding="async"
          // React 18 does not type the camelCase fetchPriority prop on <img>;
          // the lowercase spread passes the attribute straight through to the
          // DOM. Same pattern as the <link> preloads in pages/index.tsx.
          {...({ fetchpriority: 'high' } as Record<string, string>)}
        />
      </picture>

      {/* Slide 1 is a white-text slide on both breakpoints, so both carousels
          render the same dark scrim over it. */}
      <div className="absolute inset-0 bg-black/30">
        <div className="relative h-full md:mx-[3rem] md:max-w-default md:px-4">
          <div className="flex h-full flex-col items-center justify-end px-4 pb-[15px] text-center md:items-start md:justify-center md:px-0 md:pb-0 md:text-left">
            {/* One H2 whose wording matches each carousel's slide 1. Phrases never
                break inside. Mobile and lg+ render three lines; at md the first
                two phrases share a line (the hero is only 32vw tall there). */}
            <h2 className="mb-2 text-[2.375rem] font-bold leading-tight text-white md:mb-0 md:max-w-[60rem] md:text-[4.25rem] md:font-semibold lg:text-[5.5rem]">
              <span className="md:hidden">
                <span className="block whitespace-nowrap">Used Gear.</span>
                <span className="block whitespace-nowrap">Verified Sellers.</span>
                <span className="block whitespace-nowrap">Protected Orders.</span>
              </span>
              <span className="hidden md:inline">
                <span className="whitespace-nowrap">Used Gear.</span>{' '}
                <br className="hidden lg:inline" />
                <span className="whitespace-nowrap">Verified Sellers.</span>
                <br />
                <span className="whitespace-nowrap">Protected Orders.</span>
              </span>
            </h2>
            <p className="mb-4 text-[1.75rem] leading-tight text-white md:mb-0 md:mt-4 md:max-w-[42rem] md:text-[1.8rem] md:leading-[1.2] md:text-white/90">
              Shop thousands of cameras, lenses, and cinema rigs from sellers we
              verify one by one.
            </p>
            <div className="md:mt-6">
              {/* A real link, so a visitor can tap through before any
                  JavaScript has loaded. Mobile slide 1 uses the brand orange
                  (bg-primary); desktop slide 1 uses orange-500. */}
              <Link
                href="/products"
                className="inline-block rounded-[1rem] bg-primary px-[15px] py-[8px] text-[1.5rem] font-semibold text-white transition-colors hover:bg-primary-hover md:bg-orange-500 md:px-8 md:py-4 md:text-3xl md:hover:bg-orange-600"
              >
                Shop Now
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default HeroFirstFrame;
